import { useEffect, useState, useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { setActiveTenantId, getActiveTenantId } from './msalConfig';

const API = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:5295';

/**
 * Single source of truth for the signed-in user's identity, the tenants they
 * can access, and the active tenant choice. Backed by /api/v1/iam/me/* and
 * localStorage. Callers re-render whenever the active tenant changes.
 */
export function useCurrentUser() {
  const { accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = accounts[0];

  const [profile, setProfile] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTenant, setActiveTenantState] = useState(getActiveTenantId());

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setProfile(null);
      setTenants([]);
      return;
    }
    setLoading(true);
    try {
      // Snapshot the active-tenant choice as it was when THIS call began. The
      // re-fetch decision below keys off this snapshot — NOT off live
      // localStorage. Several useCurrentUser instances mount at once (App,
      // Sidebar, TenantSwitcher); if the gate read live localStorage, whichever
      // instance wrote the flag first would make the others skip their own
      // role-resolving re-fetch — leaving them with a null-tenant/null-role
      // profile (the "empty until refresh" bug). Keying off the per-call
      // snapshot makes every instance resolve the role independently.
      const startTenant = getActiveTenantId();

      const [meRes, tenantsRes] = await Promise.all([
        fetch(`${API}/api/v1/iam/me`),
        fetch(`${API}/api/v1/iam/me/tenants`),
      ]);
      const me = meRes.ok ? await meRes.json() : null;
      const t  = tenantsRes.ok ? await tenantsRes.json() : { tenants: [] };
      setTenants(t.tenants || []);

      // An existing choice wins; otherwise auto-select the sole assignment.
      const chosen = startTenant
        || ((t.tenants || []).length === 1 ? t.tenants[0].tenantId : null);

      if (chosen && !startTenant) {
        // The /me above went out without the active-tenant header, so its
        // `activeRole` came back null. Adopt the tenant and re-fetch /me WITH
        // the header so the resolver fills in the role. Idempotent across
        // instances — setActiveTenantState bails out if the value is unchanged.
        setActiveTenantId(chosen);
        setActiveTenantState(chosen);
        const meAgain = await fetch(`${API}/api/v1/iam/me`);
        setProfile(meAgain.ok ? await meAgain.json() : me);
      } else {
        if (chosen) setActiveTenantState(chosen);
        setProfile(me);
      }
    } catch (e) {
      console.error('useCurrentUser refresh failed', e);
    }
    setLoading(false);
  }, [isAuthenticated]);

  useEffect(() => { refresh(); }, [refresh]);

  const switchTenant = useCallback((tenantId) => {
    setActiveTenantId(tenantId);
    setActiveTenantState(tenantId);
    // Force a full reload — request-scoped state on every page rebuilds
    // around the new tenant cleanly without React-side cache invalidation.
    if (typeof window !== 'undefined') window.location.reload();
  }, []);

  return {
    isAuthenticated,
    account,
    email: account?.username || profile?.email || '',
    displayName: account?.name || profile?.displayName || '',
    profile,            // MyProfileDto (or null until /me resolves)
    tenants,            // TenantSwitcherEntryDto[]
    activeTenant,       // currently chosen tenant id (string | null)
    role: profile?.activeRole || null,
    isPlatformAdmin: !!profile?.isPlatformAdmin,
    canSwitchTenant: !!profile?.canSwitchTenant,
    loading,
    refresh,
    switchTenant,
  };
}
